import { useCallback } from 'react';
import { useMutation } from '@tanstack/react-query';

import { applyValidationResult } from 'pages/home/ribbon/components/sections/product/components/product-upload-wizard/utils/validation';
import {
  filterDisplayShipperItems,
  getQuestionParams,
} from 'pages/home/ribbon/components/sections/product/components/product-upload-wizard/utils/wizardProduct';

import { validateWizardForm } from 'services/product';
import { CustomNotification } from 'common/components';

export const useValidateWizardForm = (options = {}) => {
  return useMutation({
    mutationFn: (params) => validateWizardForm(params),
    onSuccess: (data, variables, context) => {
      if (options.onSuccess) {
        options.onSuccess(data, variables, context);
      }
    },
    onError: (error, variables, context) => {
      if (options.onError) {
        options.onError(error, variables, context);
      }
    },
    onSettled: (data, error, variables, context) => {
      if (options.onSettled) {
        options.onSettled(data, error, variables, context);
      }
    },
    ...options,
  });
};

export const useProductValidation = (state, actions) => {
  const { mutateAsync: validateWizardProductAsync, isLoading: isValidating } =
    useValidateWizardForm({
      onSuccess: (response) => {
        if (response?.isSuccess) {
          const updatedProducts = applyValidationResult({
            response,
            products: state.products,
          });
          actions.setProducts(updatedProducts);
        }
      },

      onError: (error) => {
        // Handle API error response
        if (error?.response?.data?.validationResults) {
          const validationResults = error.response.data.validationResults;

          const updatedProducts = state.products.map((product) => {
            const validationResult = validationResults.find(
              (result) => result.uniqueId === product.key
            );

            if (validationResult && !validationResult.passedValidation) {
              const errors = validationResult.validationResults || [];

              const errorMessages = errors.filter(
                (err) => err.messageType === 'Error'
              );
              const warningMessages = errors.filter(
                (err) => err.messageType === 'Warning'
              );

              const status =
                errorMessages.length > 0
                  ? 'error'
                  : warningMessages.length > 0
                    ? 'warning'
                    : 'success';

              const validationErrors = {};
              errors.forEach((err, index) => {
                validationErrors[`error_${index}`] = [err.message];
              });

              return {
                ...product,
                validationErrors,
                validationMessages: errors,
                status,
              };
            }

            return product;
          });

          actions.setProducts(updatedProducts);
        } else {
        }
      },
    });

  const validateWizardForm = useCallback(
    async ({ products, memberId, showMessage = true }) => {
      if (!products?.length) {
        return null;
      }

      const requestPayload = {
        ...getQuestionParams(state),
        memberId,
        wizardFormId: state.wizardFormId,
        wizardFormItems: products.map((product) => {
          const displayShipperItems = filterDisplayShipperItems(
            product.displayShipperItems
          );
          return {
            ...product,
            displayShipperItems,
          };
        }),
      };

      const response = await validateWizardProductAsync(requestPayload, {
        onSuccess: (response) => {
          if (!response?.isSuccess) {
            CustomNotification.error(response?.message ?? 'Validation failed');
          } else if (showMessage) {
            CustomNotification.info(
              'Products have been loaded. Please review your data prior to submission.'
            );
          }
        },
        onError: (error) => {
          CustomNotification.error(error?.message ?? 'Validation failed');
        },
      });

      return response;
    },
    [state, validateWizardProductAsync]
  );

  return { validateWizardForm, isValidating, validateWizardProductAsync };
};
